/**
 * AdminUnifiedInboxTab — the Unified Inbox management tab on /admin.
 *
 * Composes the connection health banner, the unified mailbox directory, and the
 * inbox settings tab (inbox list + editor + RBAC).
 *
 * The two per-provider setup wizards it used to render — `MicrosoftSetupWizard`
 * and `GoogleMailboxSetupWizard` — are replaced by one `MailboxDirectoryPanel`.
 * They read two unrelated stores through two unrelated shapes, and the Gmail one
 * scoped itself to `inboxes[0]` without saying so, so a mailbox attached to any
 * other inbox rendered as "No Gmail mailboxes attached to this inbox yet" while
 * its grant was live and its ref row existed. The directory is flat and total
 * instead, with a provider badge and an inbox badge per row.
 */
import React, { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import MailboxDirectoryPanel from './MailboxDirectoryPanel';
import InboxSettingsTab from './settings/InboxSettingsTab';
import SubscriptionExpiredBanner from '../../inbox/SubscriptionExpiredBanner';
import { reportVerifyResult } from '../../inbox/verifyResultToast';
import {
    useCancelMailboxAccess,
    useInboxes,
    useInboxStats,
    useMailboxAccessRequests,
    useMailboxDirectory,
    useMailboxTokens,
    useRemoveMailbox,
    useRequestMailboxAccess,
    useResendMailboxAccess,
    useVerifyMicrosoft,
} from '../../../hooks/useUnifiedInbox';
import { providerForMailbox } from '../../../utils/mailboxProvider';
import { mergeMailboxRows } from '../../../utils/mailboxDirectory';

export default function AdminUnifiedInboxTab() {
    const { data: inboxes = [] } = useInboxes();
    const firstInboxId = inboxes[0]?.inbox_id || null;

    // Health banner only. It reports the FIRST inbox, which is honest for a
    // single-inbox tenant and partial otherwise; per-mailbox delivery state is
    // shown on each directory row instead, where it cannot be mistaken for a
    // whole-page verdict.
    const { data: stats } = useInboxStats(firstInboxId);

    const { data: mailboxTokens = [] } = useMailboxTokens();
    const { data: msAccessRequests = [] } = useMailboxAccessRequests('microsoft');
    const { data: googleAccessRequests = [] } = useMailboxAccessRequests('google');
    const accessRequests = useMemo(
        () => [...msAccessRequests, ...googleAccessRequests],
        [msAccessRequests, googleAccessRequests]
    );

    const { data: directoryData, isLoading: directoryLoading } = useMailboxDirectory();
    const rows = useMemo(
        () =>
            mergeMailboxRows({
                directory: directoryData?.data || [],
                accessRequests,
                inboxes,
            }),
        [directoryData, accessRequests, inboxes]
    );

    const verify = useVerifyMicrosoft();
    const requestMailboxAccess = useRequestMailboxAccess();
    const resendMailboxAccess = useResendMailboxAccess();
    const cancelMailboxAccess = useCancelMailboxAccess();
    const removeMailbox = useRemoveMailbox();

    const runVerify = useCallback(async () => {
        try {
            reportVerifyResult(await verify.mutateAsync());
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Verification failed');
        }
    }, [verify]);

    const handleRequestAccess = useCallback(
        // `provider` comes from the row. It is omitted entirely when unknown so
        // the control plane resolves the platform from MX — substituting a
        // client-side default is what misrouted Workspace custom domains.
        async (mail, provider) => {
            try {
                await requestMailboxAccess.mutateAsync({
                    mailboxEmail: mail,
                    inboxId: firstInboxId || '',
                    ...(provider ? { provider } : {}),
                });
                toast.success(`Access request sent to ${mail}`);
            } catch (err) {
                const detail = err?.response?.data?.detail;
                const msg =
                    detail === 'control_plane_unreachable'
                        ? 'Cannot reach the control plane. Please try again later.'
                        : detail === 'control_plane_rejected'
                        ? 'The request was rejected by the server. Please contact support.'
                        : `Could not send access request: ${detail || 'Unknown error'}`;
                toast.error(msg);
            }
        },
        [requestMailboxAccess, firstInboxId]
    );

    const handleResend = useCallback(
        async (mail, provider) => {
            try {
                await resendMailboxAccess.mutateAsync({
                    mail,
                    provider:
                        provider ||
                        providerForMailbox(mail, { mailboxTokens, accessRequests }),
                });
                toast.success(`Access request resent to ${mail}`);
            } catch (err) {
                const detail = err?.response?.data?.detail;
                toast.error(`Could not resend: ${detail || 'Unknown error'}`);
            }
        },
        [resendMailboxAccess, mailboxTokens, accessRequests]
    );

    const handleCancel = useCallback(
        async (mail, provider) => {
            try {
                await cancelMailboxAccess.mutateAsync({
                    mail,
                    provider:
                        provider ||
                        providerForMailbox(mail, { mailboxTokens, accessRequests }),
                });
                toast.success(`Request cancelled for ${mail}`);
            } catch (err) {
                const detail = err?.response?.data?.detail;
                toast.error(`Could not cancel request: ${detail || 'Unknown error'}`);
            }
        },
        [cancelMailboxAccess, mailboxTokens, accessRequests]
    );

    const handleRemove = useCallback(
        async row => {
            try {
                const result = await removeMailbox.mutateAsync({
                    provider: row.provider,
                    connectionId: row.connection_id || undefined,
                    mail: row.mail || row.email,
                });
                const detached = result?.data?.detached ?? 0;
                toast.success(
                    `${row.email} removed — token deleted` +
                        (detached ? `, detached from ${detached} inbox(es)` : '')
                );
            } catch (err) {
                const detail = err?.response?.data?.detail || '';
                // The distro guard is the one refusal an admin can act on, so it
                // gets a sentence rather than a raw code.
                if (detail.startsWith('mailbox_is_distro:')) {
                    toast.error(
                        `${row.email} is the reply address for "${detail.split(':')[1]}". ` +
                            'Change that inbox’s distro address first.'
                    );
                    return;
                }
                toast.error(`Could not remove ${row.email}: ${detail || 'Unknown error'}`);
            }
        },
        [removeMailbox]
    );

    const handleResendAll = useCallback(async () => {
        const pending = rows.filter(r => r.status === 'pending');
        if (pending.length === 0) return;
        let succeeded = 0;
        let lastError = null;
        for (const row of pending) {
            try {
                await resendMailboxAccess.mutateAsync({
                    mail: row.email,
                    provider: row.provider,
                });
                succeeded += 1;
            } catch (err) {
                lastError = err?.response?.data?.detail || 'Unknown error';
            }
        }
        if (succeeded) {
            toast.success(`Resent access requests to ${succeeded} mailbox(es)`);
        }
        if (succeeded < pending.length) {
            toast.error(`Some requests failed: ${lastError}`);
        }
    }, [rows, resendMailboxAccess]);

    const isBusy =
        requestMailboxAccess.isPending ||
        resendMailboxAccess.isPending ||
        cancelMailboxAccess.isPending ||
        removeMailbox.isPending;

    return (
        <div className="space-y-6" data-testid="admin-unified-inbox-tab">
            {/* Connection health: subscription banner + verify */}
            <SubscriptionExpiredBanner
                stats={stats}
                onVerify={runVerify}
                isVerifying={verify.isPending}
            />

            {/* Every mailbox, both providers, with provider + inbox badges */}
            <MailboxDirectoryPanel
                rows={rows}
                loading={directoryLoading}
                onRequestAccess={handleRequestAccess}
                onResend={handleResend}
                onCancel={handleCancel}
                onRemove={handleRemove}
                onResendAll={handleResendAll}
                isBusy={isBusy}
            />

            {/* Inbox settings: list, editor, RBAC */}
            <InboxSettingsTab />
        </div>
    );
}
